import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Shield, Phone, MapPin, Cloud, User } from 'lucide-react';
import './GuidePage.css';

export default function SafetyGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🦺 Sicherheit beim Radfahren in Schottland</h1>
        
        <div className="warning-box">
          <AlertTriangle size={24} />
          <div>
            <strong>Wichtig:</strong> Schottland bietet fantastische Radstrecken, aber die Bedingungen können herausfordernd sein. 
            Sicherheit geht immer vor!
          </div>
        </div>

        <section>
          <h2>1. Helm & Schutzausrüstung</h2>
          
          <h3>Helmpflicht</h3>
          <p>
            In Großbritannien besteht <strong>keine gesetzliche Helmpflicht</strong> für Erwachsene. 
            Trotzdem ist das Tragen eines Helms <strong>dringend empfohlen</strong> und bei geführten Touren oft Pflicht.
          </p>

          <div className="info-box">
            <h4>🛡️ Helm-Standards</h4>
            <ul>
              <li><strong>EN 1078:</strong> Europäischer Standard für Fahrradhelme</li>
              <li><strong>CPSC:</strong> US-Standard (auch in UK anerkannt)</li>
              <li><strong>MIPS:</strong> Zusätzlicher Rotationsschutz (empfohlen für MTB)</li>
            </ul>
          </div>

          <h3>Zusätzliche Schutzausrüstung</h3>
          <ul>
            <li><strong>Handschuhe:</strong> Schutz bei Stürzen, besserer Grip</li>
            <li><strong>Knie- & Ellbogenschoner:</strong> Für technische MTB-Trails</li>
            <li><strong>Warnweste:</strong> In Schottland nicht Pflicht, aber sehr empfohlen (Nebel!)</li>
            <li><strong>Rückenprotektor:</strong> Für Downhill und technische Trails</li>
          </ul>
        </section>

        <section>
          <h2>2. Sichtbarkeit</h2>
          
          <h3>Lichter (Pflicht bei Dunkelheit)</h3>
          <p><strong>Gesetzliche Anforderungen in UK:</strong></p>
          <ul>
            <li>Weißes Frontlicht (vorne)</li>
            <li>Rotes Rücklicht (hinten)</li>
            <li>Beide müssen bei Dunkelheit und schlechter Sicht eingeschaltet sein</li>
            <li>Blinkmodus erlaubt (50-240 Blitze/Minute)</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 Schottland-Spezial: Tagfahrlicht!</h3>
            <p>
              Aufgrund häufiger Nebel, niedriger Wolken und früher Dämmerung empfehlen wir:
              <strong> Licht auch tagsüber einschalten!</strong> Besonders in den Highlands.
            </p>
          </div>

          <h3>Reflektoren & Kleidung</h3>
          <ul>
            <li>Reflektoren an Speichen, Pedalen und Gepäck</li>
            <li>Reflektierende oder helle Kleidung (Gelb, Orange, Neonfarben)</li>
            <li>Reflektierende Beinmanschetten</li>
          </ul>
        </section>

        <section>
          <h2>3. Verkehrsregeln Großbritannien</h2>
          
          <div className="warning-box">
            <AlertTriangle size={24} />
            <strong>Linksverkehr!</strong> Das ist die größte Umstellung für Kontinentaleuropäer.
          </div>

          <h3>Wichtigste Regeln</h3>
          <ul>
            <li><strong>Links fahren, rechts überholen</strong> (umgekehrt zu DE/CH/AT)</li>
            <li><strong>Kreisverkehr:</strong> Im Uhrzeigersinn, Vorfahrt hat, wer im Kreis ist</li>
            <li><strong>Single-Track Roads:</strong> Passstellen nutzen, Vorfahrtsregeln beachten</li>
            <li><strong>Rechts vor Links</strong> gibt es nicht! Vorfahrtszeichen beachten</li>
            <li><strong>Zebrastreifen:</strong> Autos müssen anhalten (wird streng beachtet)</li>
          </ul>

          <h3>Radwege & Straßennutzung</h3>
          <ul>
            <li><strong>Keine Radwegpflicht:</strong> Auch bei vorhandenem Radweg darf die Straße genutzt werden</li>
            <li><strong>Shared Use Paths:</strong> Fußgänger & Radfahrer (Rücksicht nehmen!)</li>
            <li><strong>Motorways (M-Straßen):</strong> Für Radfahrer verboten</li>
            <li><strong>A-Roads:</strong> Erlaubt, aber oft stark befahren (Vorsicht!)</li>
          </ul>
        </section>

        <section>
          <h2>4. Wetterbedingungen</h2>
          
          <h3>Das schottische Wetter-Risiko</h3>
          <p>
            <strong>"There's no such thing as bad weather, only inappropriate clothing"</strong> - 
            schottisches Sprichwort, das du ernst nehmen solltest!
          </p>

          <div className="info-box">
            <h4><Cloud size={20} /> Wetter-Herausforderungen</h4>
            <ul>
              <li><strong>Regen:</strong> Kann jederzeit kommen, auch bei Sonnenschein</li>
              <li><strong>Wind:</strong> Besonders an der Küste und in den Highlands (bis zu Sturmböen)</li>
              <li><strong>Nebel:</strong> Kann innerhalb von Minuten aufziehen</li>
              <li><strong>Temperatur:</strong> Kann um 10-15°C schwanken am selben Tag</li>
              <li><strong>Schnee:</strong> In höheren Lagen auch im Sommer möglich</li>
            </ul>
          </div>

          <h3>Vor der Tour prüfen</h3>
          <ul>
            <li><strong>Met Office:</strong> metoffice.gov.uk (offizielle UK-Wettervorhersage)</li>
            <li><strong>Mountain Weather:</strong> mwis.org.uk (für Highlands)</li>
            <li><strong>Wind-Warnung:</strong> Ab Windstärke 7 (50+ km/h) Tour überdenken</li>
            <li><strong>Wetter-Cutoff:</strong> Bei Sturmwarnung nicht fahren!</li>
          </ul>

          <h3>Unterwegs reagieren</h3>
          <ul>
            <li>Bei plötzlichem Nebel: Geschwindigkeit reduzieren, Licht einschalten</li>
            <li>Bei Gewitter: Nicht auf exponierten Höhen, Schutz suchen</li>
            <li>Bei starkem Wind: In Böen Lenker fest halten, auf Seitenwind achten</li>
            <li>Bei Regen: Bremswege verlängern sich! Vorsichtig fahren</li>
          </ul>
        </section>

        <section>
          <h2>5. Straßenzustand & Hindernisse</h2>
          
          <h3>Single-Track Roads</h3>
          <p>
            Schmale Einspurstraßen mit Passstellen - typisch für Highlands und Inseln.
          </p>
          <ul>
            <li><strong>Passstellen:</strong> Kleine Buchten zum Ausweichen</li>
            <li><strong>Regel:</strong> Wer näher an der Passstelle ist, fährt vor</li>
            <li><strong>Für Radfahrer:</strong> Immer rechts ranfahren und Autos passieren lassen</li>
            <li><strong>Vorsicht:</strong> Autos können schnell kommen - immer aufmerksam sein</li>
          </ul>

          <h3>Straßenbelag</h3>
          <ul>
            <li><strong>Schlaglöcher:</strong> Häufig, besonders nach Winter (März-Mai schlimm)</li>
            <li><strong>Kies:</strong> Auf kleinen Straßen lose Steine möglich</li>
            <li><strong>Cattle Grids:</strong> Viehgitter (langsam überqueren!)</li>
            <li><strong>Fords:</strong> Wasserdurchfahrten (nach Regen meiden)</li>
          </ul>

          <h3>Tiere auf der Straße</h3>
          <ul>
            <li><strong>Schafe:</strong> Überall! Können plötzlich auf die Straße laufen</li>
            <li><strong>Highland Cattle:</strong> Imposant aber friedlich (Abstand halten)</li>
            <li><strong>Hirsche:</strong> Besonders in Dämmerung aktiv</li>
            <li><strong>Vögel:</strong> Fasane fliegen plötzlich auf</li>
          </ul>
        </section>

        <section>
          <h2>6. Orientierung & Navigation</h2>
          
          <h3>GPS & Karten</h3>
          <ul>
            <li><strong>GPS-Gerät:</strong> Zuverlässiger als Smartphone (Akku!)</li>
            <li><strong>Offline-Karten:</strong> Mobilfunknetz ist nicht überall verfügbar</li>
            <li><strong>Backup-Karte:</strong> Papier-Karte (Ordnance Survey) mitführen</li>
            <li><strong>Kompass:</strong> Bei Nebel oder GPS-Ausfall</li>
          </ul>

          <h3>Mobilfunk-Empfang</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>In den Highlands gibt es große Funklöcher!</strong> Besonders:
            <ul>
              <li>Westküste (Applecross, Torridon)</li>
              <li>Nord-Highlands (NC500 Nordteil)</li>
              <li>Glencoe & Rannoch Moor</li>
              <li>Innere Hebriden</li>
            </ul>
          </div>

          <h3>Notfall ohne Handy-Empfang</h3>
          <ul>
            <li>Route vorher jemandem mitteilen</li>
            <li>In Notunterkünften (Bothies) nach Hilfe fragen</li>
            <li>Hauptstraßen aufsuchen (mehr Verkehr = mehr Hilfe)</li>
            <li>Emergency Locator: what3words App (auch offline nutzbar)</li>
          </ul>
        </section>

        <section>
          <h2>7. Notfälle & Erste Hilfe</h2>
          
          <h3>Wichtige Nummern</h3>
          <div className="info-box">
            <h4><Phone size={20} /> Notrufnummern UK</h4>
            <ul>
              <li><strong>999:</strong> Polizei, Feuerwehr, Ambulanz (Hauptnotruf)</li>
              <li><strong>112:</strong> EU-weiter Notruf (funktioniert auch in UK)</li>
              <li><strong>101:</strong> Polizei (nicht dringend)</li>
              <li><strong>111:</strong> NHS Gesundheitsberatung (nicht lebensbedrohlich)</li>
            </ul>
          </div>

          <h3>Mountain Rescue</h3>
          <p>
            Für Unfälle in abgelegenen Gebieten: <strong>999 und "Mountain Rescue" verlangen</strong>
          </p>
          <ul>
            <li>Kostenlos (wird von Freiwilligen betrieben)</li>
            <li>Spezialisiert auf Highlands & schwieriges Gelände</li>
            <li>Standort angeben: GPS-Koordinaten oder what3words</li>
          </ul>

          <h3>Erste-Hilfe-Set</h3>
          <div className="checklist">
            <div className="check-item">✓ Verbandsmaterial (Pflaster, Kompressen)</div>
            <div className="check-item">✓ Desinfektionsmittel</div>
            <div className="check-item">✓ Blasenpflaster</div>
            <div className="check-item">✓ Schmerzmittel (Paracetamol, Ibuprofen)</div>
            <div className="check-item">✓ Rettungsdecke</div>
            <div className="check-item">✓ Zeckenzange</div>
            <div className="check-item">✓ Sonnencreme & Lippenbalsam</div>
          </div>

          <h3>Häufigste Rad-Verletzungen</h3>
          <ul>
            <li><strong>Stürze:</strong> Schürfwunden reinigen, desinfizieren</li>
            <li><strong>Unterkühlung:</strong> Trockene Kleidung anziehen, warmen Tee trinken</li>
            <li><strong>Dehydrierung:</strong> Regelmäßig trinken (auch bei Regen!)</li>
            <li><strong>Sonnenbrand:</strong> Auch bei bewölktem Himmel möglich (UV-Strahlung!)</li>
          </ul>
        </section>

        <section>
          <h2>8. Allein vs. Gruppe</h2>
          
          <h3>Solo-Radfahren</h3>
          <p><strong>Vorteile:</strong> Flexibilität, eigenes Tempo</p>
          <p><strong>Nachteile:</strong> Keine Hilfe bei Panne oder Unfall</p>
          
          <div className="warning-box">
            <strong>Wenn du allein fährst:</strong>
            <ul>
              <li>Route vorher jemandem mitteilen</li>
              <li>Täglich bei Familie/Freunden melden</li>
              <li>Notfall-Kontakt im Handy speichern (ICE - In Case of Emergency)</li>
              <li>Größere Sicherheitsreserve einplanen</li>
            </ul>
          </div>

          <h3>Gruppenfahren</h3>
          <ul>
            <li><strong>Sicherer:</strong> Gegenseitige Hilfe bei Pannen</li>
            <li><strong>Kommunikation:</strong> Handzeichen für Hindernisse, Stopps</li>
            <li><strong>Hintereinanderfahren:</strong> Bei schmalen Straßen</li>
            <li><strong>Tempo:</strong> Am langsamsten Mitglied orientieren</li>
          </ul>
        </section>

        <section>
          <h2>9. Versicherung</h2>
          
          <h3>Empfohlene Versicherungen</h3>
          <ul>
            <li><strong>Reiseversicherung:</strong> Medizinische Kosten, Rücktransport</li>
            <li><strong>Haftpflichtversicherung:</strong> Schäden an Dritten</li>
            <li><strong>Fahrradversicherung:</strong> Diebstahl, Beschädigung (besonders bei teurem Bike)</li>
            <li><strong>Reiserücktrittsversicherung:</strong> Bei gebuchten Touren</li>
          </ul>

          <h3>NHS (National Health Service)</h3>
          <p>
            UK hat ein öffentliches Gesundheitssystem. <strong>EU-Bürger:</strong> EHIC (European Health Insurance Card) 
            mitführen für Notfallbehandlung.
          </p>
          <p>
            <strong>Schweizer:</strong> Zusätzliche Auslandskrankenversicherung empfohlen (UK ist nicht in EU-EHIC)
          </p>
        </section>

        <section>
          <h2>10. Diebstahlschutz</h2>
          
          <h3>Fahrrad sichern</h3>
          <ul>
            <li><strong>Hochwertiges Schloss:</strong> Bügelschloss + Kabelschloss</li>
            <li><strong>Anschließen:</strong> Rahmen + Laufrad an festem Objekt</li>
            <li><strong>Belebte Orte:</strong> Gut einsehbare Stellplätze wählen</li>
            <li><strong>Wertsachen:</strong> Nie am Rad lassen (Taschen, GPS, etc.)</li>
          </ul>

          <h3>In Unterkünften</h3>
          <ul>
            <li>Nach abschließbarem Raum fragen (viele B&Bs bieten das an)</li>
            <li>Fahrrad mit ins Zimmer nehmen (vorher fragen)</li>
            <li>Gepäck nicht unbeaufsichtigt am Rad lassen</li>
          </ul>
        </section>

        <div className="pro-tip">
          <h3>🎯 Die 10 goldenen Sicherheitsregeln</h3>
          <ol>
            <li>Helm immer tragen</li>
            <li>Licht auch tagsüber (Nebel!)</li>
            <li>Wetter vor Tour checken</li>
            <li>Route jemandem mitteilen</li>
            <li>Offline-Karten dabei haben</li>
            <li>Erste-Hilfe-Set mitführen</li>
            <li>Bei Nebel/Sturm Tour abbrechen</li>
            <li>Regelmäßig trinken & essen</li>
            <li>Notfallnummer 999 kennen</li>
            <li>Versicherung abschließen</li>
          </ol>
        </div>

        <div className="download-section">
          <h3>📥 Sicherheits-Guide herunterladen</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Sicherheits-Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
